import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    Avatar,
    Box,
    ClickAwayListener,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';

/**
 * Search-based business selector with a pinned "Create New" action at the bottom.
 *
 * Type to search, results appear in a floating dropdown (portalled to body so it
 * is never clipped by parent overflow). "Create New" is pinned at the bottom of
 * the results, matching the QuickAction assign-to pattern.
 *
 * Props:
 *   businesses              Array of business objects (parent handles loading).
 *   value                   { businessId: string|null, isCreatingNew: boolean }
 *   onChange                ({ businessId, isCreatingNew }) => void
 *   filter                  Optional (business) => boolean - applied before search.
 *   label                   Placeholder text (default "Search companies...").
 *   createNewLabel          Bottom-row label (default "Create New Company").
 *   required, error, helperText  Forwarded to the TextField.
 */
const BusinessSelector = ({
    businesses = [],
    value = { businessId: null, isCreatingNew: false },
    onChange,
    filter,
    label = 'Search companies...',
    createNewLabel = 'Create New Company',
    required = false,
    error = false,
    helperText = '',
}) => {
    const inputRef = useRef(null);
    const [search, setSearch] = useState('');
    const [focused, setFocused] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    const filteredBusinesses = useMemo(
        () => businesses.filter((business) => (filter ? filter(business) : true)),
        [businesses, filter]
    );

    const selectedBusiness = filteredBusinesses.find(
        (business) => business.businessId === value.businessId
    );

    const searchResults = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return [];
        return filteredBusinesses.filter((business) =>
            business.businessName?.toLowerCase().includes(term)
        );
    }, [filteredBusinesses, search]);

    const showDropdown = focused;

    const updatePosition = useCallback(() => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownPos({
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
            });
        }
    }, []);

    useEffect(() => {
        if (showDropdown) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [showDropdown, updatePosition]);

    const handleSelect = (business) => {
        onChange?.({ businessId: business.businessId, isCreatingNew: false });
        setSearch('');
        setFocused(false);
    };

    const handleCreateNew = () => {
        onChange?.({ businessId: null, isCreatingNew: true });
        setSearch('');
        setFocused(false);
    };

    const handleClickAway = () => setFocused(false);

    const openSearch = () => {
        setFocused(true);
        setSearch('');
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box>
                {!value.isCreatingNew && selectedBusiness && !focused && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            borderRadius: '8px',
                            backgroundColor: '#f8fafc',
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 28,
                                height: 28,
                                fontSize: '0.8rem',
                                bgcolor: 'primary.main',
                            }}
                        >
                            {selectedBusiness.businessName?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                            {selectedBusiness.businessName}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                fontWeight: 500,
                                '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={openSearch}
                        >
                            Change
                        </Typography>
                    </Box>
                )}

                {value.isCreatingNew && !focused && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'primary.light',
                            borderRadius: '8px',
                            backgroundColor: 'rgba(22, 82, 240, 0.04)',
                        }}
                    >
                        <AddIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: 'primary.main', flex: 1 }}
                        >
                            {createNewLabel}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                fontWeight: 500,
                                '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={openSearch}
                        >
                            Change
                        </Typography>
                    </Box>
                )}

                {(focused || (!selectedBusiness && !value.isCreatingNew)) && (
                    <TextField
                        ref={inputRef}
                        fullWidth
                        size="small"
                        placeholder={label}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onFocus={() => setFocused(true)}
                        autoFocus={focused}
                        required={required}
                        error={error}
                        helperText={helperText}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            sx={{ color: 'grey.400', fontSize: '1.2rem' }}
                                        />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                '& fieldset': { borderColor: 'grey.200' },
                                '&:hover fieldset': { borderColor: 'grey.300' },
                            },
                        }}
                    />
                )}

                {showDropdown &&
                    ReactDOM.createPortal(
                        <Box
                            sx={{
                                position: 'fixed',
                                zIndex: 1400,
                                borderRadius: '8px',
                                backgroundColor: '#fff',
                                border: '1px solid',
                                borderColor: 'grey.200',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: 320,
                            }}
                            style={{
                                top: dropdownPos.top,
                                left: dropdownPos.left,
                                width: dropdownPos.width,
                            }}
                        >
                            {searchResults.length > 0 && (
                                <List sx={{ overflow: 'auto', flexGrow: 1, py: 0 }}>
                                    {searchResults.map((business) => (
                                        <ListItem
                                            key={business.businessId}
                                            onClick={() => handleSelect(business)}
                                            sx={{
                                                cursor: 'pointer',
                                                py: 1,
                                                px: 2,
                                                '&:hover': { backgroundColor: 'action.hover' },
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    fontSize: '0.8rem',
                                                    mr: 1.5,
                                                    bgcolor: 'primary.main',
                                                }}
                                            >
                                                {business.businessName?.[0]?.toUpperCase() || '?'}
                                            </Avatar>
                                            <ListItemText
                                                primary={business.businessName}
                                                slotProps={{
                                                    primary: { variant: 'body2', fontWeight: 500 },
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                            {search.trim() && searchResults.length === 0 && (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No companies found
                                    </Typography>
                                </Box>
                            )}

                            <Box
                                onClick={handleCreateNew}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderTop: '1px solid',
                                    borderColor: 'grey.100',
                                    flexShrink: 0,
                                    '&:hover': { backgroundColor: 'action.hover' },
                                }}
                            >
                                <AddIcon
                                    sx={{ fontSize: '1.2rem', mr: 1, color: 'primary.main' }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, color: 'primary.main' }}
                                >
                                    {createNewLabel}
                                </Typography>
                            </Box>
                        </Box>,
                        document.body
                    )}
            </Box>
        </ClickAwayListener>
    );
};

export default BusinessSelector;
